import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { staleGroupProductCurrencyResyncs } from '@core/hooks/useProductSection';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import type { QuoteDrawerFormikType } from '../utils';

/**
 * Product price currencies are stamped at edit/prefill time from data that can
 * be missing or unloaded (the project-product prefill never hydrates the
 * nested plant, and edits before plant data loads fall back to the tenant
 * default), which leaves a non-USD plant's products carrying the wrong code
 * and the server rejecting the quote as mixed-currency. This effect re-stamps
 * every location group's product rows from the group's selected plant, and
 * stays mounted for the whole drawer so collapsed sections are covered too.
 * Renders nothing.
 *
 * Mounted unconditionally (including read-only): a stale stamp is wrong for
 * display too, so the correction should run regardless. Dirtying the form in
 * edit mode is intentional — it lets the correction persist via Save. Reset
 * restores the initial (stale) values, but the effect deps include both
 * product arrays, so it immediately re-runs and re-stamps.
 */
export const ProductCurrencyResyncEffect = (): null => {
  const formikBag = useFormikContext<QuoteDrawerFormikType>();
  const { mainProductsByPlants, additionalProductsByPlants } = formikBag.values;

  // Unlike the location selectors, archived plants are included: a quote
  // prefilled from a project can reference products on a since-archived
  // plant, and that plant's currency is still the truth for its rows. Params
  // are omitted so this shares the unfiltered `GET plants` cache entry already
  // fetched elsewhere in the drawer rather than issuing a second identical call.
  const { data: plantList } = useSlabQuery('GET plants', {});

  useEffect(() => {
    if (plantList === undefined) {
      return;
    }
    const plantCurrencyByID = new Map(plantList.items.map((p) => [p.id, p.currencyCode]));
    const getPlantCurrencyCode = (plantId: string): string | null =>
      plantCurrencyByID.get(plantId) ?? null;

    (
      [
        ['mainProductsByPlants', mainProductsByPlants],
        ['additionalProductsByPlants', additionalProductsByPlants],
      ] as const
    ).forEach(([sectionName, groups]) => {
      const resyncs = staleGroupProductCurrencyResyncs(groups ?? [], getPlantCurrencyCode);
      if (resyncs.length === 0) {
        return;
      }

      // Rebuild the section array once and write it with a single
      // SetFormikValue, so the whole-form validation runs once per section
      // instead of twice per stale row.
      const updatedGroups = (groups ?? []).map((group, groupIndex) => {
        const groupResyncs = resyncs.filter((r) => r.groupIndex === groupIndex);
        if (groupResyncs.length === 0) {
          return group;
        }
        const currencyByProductIndex = new Map(
          groupResyncs.map((r) => [r.productIndex, r.currencyCode]),
        );
        return {
          ...group,
          products: group.products.map((product, productIndex) => {
            const currencyCode = currencyByProductIndex.get(productIndex);
            if (currencyCode === undefined) {
              return product;
            }
            return {
              ...product,
              price: { ...product.price, currency: currencyCode },
              ...(product.originalPrice
                ? { originalPrice: { ...product.originalPrice, currency: currencyCode } }
                : {}),
            };
          }),
        };
      });

      SetFormikValue(formikBag, sectionName, updatedGroups);
    });
  }, [mainProductsByPlants, additionalProductsByPlants, plantList]);

  return null;
};
